import React, { ReactNode } from 'react';
import { AppBar, Box, Button, SxProps, Theme, Toolbar, Typography } from '@mui/material';

import devTools from '@paragon/dev-tools';
import { upgradePlanColorGradient } from '@paragon/login-tools';
import { ThemedLogo } from '@paragon/themes';

import { LinearTaskSpinner } from './LinearTaskSpinner';

interface Props {
  readonly children?: ReactNode;

  /**
   * The title of the header (optional, defaults to no title).
   */
  readonly text?: string | React.JSX.Element;

  /**
   * Whether to show the title at all screen sizes. False by default as we don't want a mobile title in most clients.
   */
  readonly showTitleAtAllSizes?: boolean;

  /**
   * Show button that calls the user to upgrade their account; requires `upgradeAccountAction` to be set also
   */
  readonly showUpgradeAccountButton?: boolean;

  /**
   * The function called when the user clicks the upgrade button; only applicable if `showUpgradeAccountButton` === true
   */
  readonly upgradeAccountAction?: () => void;

  readonly upgradeAccountButtonText?: string;

  /**
   * Specifies which client logo will appear (optional, defaults to Paragon Logo).
   */
  readonly clientLogoUrl?: string;

  /**
   * If true, the client logo will not be shown.
   */
  readonly hideClientLogo?: boolean;

  /**
   * Specifies the alt text to use for the client logo
   */
  readonly clientName?: string;

  /**
   * Job grid does not use spinnerMessages. This is used to force the spinner.
   */
  readonly forceSpinner?: boolean;

  /**
   * This is used to alter the spinner color if needed.
   */
  readonly spinnerColor?: 'primary' | 'secondary';

  /**
   * @default true
   */
  readonly renderDevToolsButton?: boolean;

  /**
   * If provided, will be applied to the MUI Toolbar that is rendered.
   */
  readonly toolbarSx?: SxProps<Theme>;
}

function reloadClient() {
  window.location.href = window.location.origin;
}

/**
 * The standard material header component used in paragon clients.  Includes a built-in task spinner.  Icon buttons or
 *   other components to include in the right-hand side of the header can be provided as children.
 */
export function ParagonHeader({
  children,
  text,
  showTitleAtAllSizes = false,
  showUpgradeAccountButton,
  upgradeAccountAction,
  upgradeAccountButtonText = 'Upgrade',
  clientLogoUrl,
  hideClientLogo,
  clientName,
  forceSpinner,
  spinnerColor,
  renderDevToolsButton = true,
  toolbarSx,
}: Props) {
  return (
    <AppBar position="static" sx={{ zIndex: 'appBar', userSelect: 'none', backgroundImage: 'none' }}>
      <Toolbar
        sx={[
          {
            paddingX: { xs: 1, md: 2 },
            justifyContent: { xs: 'flex-start', md: 'space-between' },
          },
          ...(Array.isArray(toolbarSx) ? toolbarSx : [toolbarSx]),
        ]}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!hideClientLogo && (
            <ThemedLogo
              clientLogoUrl={clientLogoUrl}
              clientName={clientName}
              width={64}
              height={64}
              sx={{ cursor: 'pointer' }}
              onClick={reloadClient}
            />
          )}

          {text && (
            <>
              <Typography
                variant="h1"
                sx={{
                  display: showTitleAtAllSizes ? undefined : { xs: 'none', lg: 'initial' },
                  marginLeft: 1,
                  letterSpacing: 3, // 1 is applied that the theme level but this header needs 3
                  whiteSpace: 'nowrap', // at this font size wrapping causes THICC header syndrome
                }}
              >
                {typeof text === 'string' ? text.toUpperCase() : text}
              </Typography>
              {showUpgradeAccountButton && upgradeAccountAction && (
                <Button
                  sx={{
                    ml: 1,
                    background: upgradePlanColorGradient,
                    color: 'white',
                  }}
                  size="small"
                  onClick={upgradeAccountAction}
                >
                  {upgradeAccountButtonText}
                </Button>
              )}
            </>
          )}
        </Box>

        {renderDevToolsButton ? (
          <div>
            {children}
            {devTools.renderDevToolsButton()}
          </div>
        ) : (
          children
        )}
      </Toolbar>
      <LinearTaskSpinner spinnerColor={spinnerColor} forceSpinner={forceSpinner} />
    </AppBar>
  );
}
